import type { Dictionary, HighlightsMessages } from '../models'

import {
  AuditActionEnum,
  FeatureIdEnum,
  HighlightsFilterEnum,
  KeywordsModeEnum,
  LocaleEnum,
} from '../enums'

const eDocsPortal = 'Playboard docs'

const HIGHLIGHTS_MESSAGES_EN: HighlightsMessages = {
  headerTitle: 'You’re viewing:',
  filter: {
    default: 'All filters & all categories',
    title: {
      [HighlightsFilterEnum.GLOBAL]: 'Global',
      [HighlightsFilterEnum.FILTER]: 'Filters',
      [HighlightsFilterEnum.CATEGORY]: 'Categories',
    },
    maxFiltersAllowed: {
      [HighlightsFilterEnum.FILTER]: 'Add {maxFilters} filters max.',
      [HighlightsFilterEnum.CATEGORY]: 'Add {maxFilters} categories max.',
    },
    maxFiltersReached: {
      [HighlightsFilterEnum.FILTER]: '{maxFilters} filters already added.',
      [HighlightsFilterEnum.CATEGORY]: '{maxFilters}  categories already added.',
    },
    disabled: 'One or more elements of this filter no longer exist.',
  },
  aside: {
    filters: {
      title: 'Filters',
      accept: 'Save',
      duplicatedFilterError: 'This filter combination already exists.',
    },
    categories: {
      title: 'Categories',
      accept: 'Save',
      duplicatedCategoryError: 'The category selected is already a filter.',
      idLabel: '(ID: {idLabel} )',
    },
  },
  widget: {
    welcome: {
      titles: {
        morning: 'Good morning,',
        afternoon: 'Good afternoon,',
        link: eDocsPortal,
      },
      description:
        'You can review activity data, identify opportunities for improvement, and take action to optimize the search experience!',
      link: 'https://docs.empathy.co/play-with-empathy-platform',
      today: 'Today',
      audit: {
        default: '{feature} {action}',
        simple: '{feature} {action} for {query}',
        multiple: '{feature} for {query} and {count} {action}',
        actions: {
          [AuditActionEnum.CREATE]: 'created',
          [AuditActionEnum.UPDATE]: 'updated',
          [AuditActionEnum.DELETE]: 'deleted',
          [AuditActionEnum.DOWNLOAD]: 'downloaded',
          [AuditActionEnum.SEARCH]: 'searched',
        },
      },
    },
    opportunities: {
      title: 'My opportunities',
      description:
        'Discover how to optimize your search performance' +
        ' and relevance leveraging the most appropriate Empathy ' +
        'Platform Playboard tool based on different search KPIs',
      actions: {
        close: 'Close',
      },
      date: 'Today',
      tooltip: {
        tools: {
          title: 'Why don’t you use…?',
          [FeatureIdEnum.PROMOTION]: {
            clicks:
              'Products on the first results page aren’t relevant to your shoppers.<br>' +
              'TIP! Feature relevant products on top of the SERP.',
            findability:
              'Too many results for shoppers to find what they want?<br>TIP! Make relevant products stand out on top of the SERP.',
          },
          [FeatureIdEnum.CONFIGURATION]: {
            clicks:
              'No clicks for the products on the first result page?<br>' +
              'TIP! Boost relevant products to the top.',
            findability:
              'Shoppers can’t find easily what they’re looking for on the SERP.<br>' +
              'TIP! Boost relevant products to the first positions.',
          },
          [FeatureIdEnum.SYNONYM]: {
            noResults:
              'The query returns few results.<br>TIP! Try to associate alternative search terms with the query.',
            clicks:
              'The results for this query aren’t pretty relevant.<br>TIP! Define alternative search terms to the query.',
          },
          [FeatureIdEnum.RELATED_TAGS]:
            'Shoppers use broad queries that lead to loads of results.<br>' +
            'TIP! Set up related tags to help shoppers refine the query.',
          [FeatureIdEnum.REDIRECTION]:
            'Shoppers are looking for topics that aren’t related to your catalogue.<br>' +
            'TIP! Redirect shoppers to a relevant URL.',
        },
        evolution: {
          title: 'Query performance evolution',
        },
      },
    },
    kpis: {
      title: 'How is search performing?',
      date: 'Last week ({period})',
      link: 'https://docs.empathy.co/explore-empathy-platform/diagram/play/play-analytics.html',
    },
    now: {
      title: 'What are shoppers seeking?',
      time: 'Last 15 mins',
      link: 'https://docs.empathy.co/explore-empathy-platform/analyze-search-and-discovery/use-now.html?utm_source=carousel-1',
    },
    keywords: {
      title: 'What are the latest trends?',
      subtitle: 'Top {count} terms by {mode}',
      modes: {
        [KeywordsModeEnum.GROWTH]: 'Growth',
        [KeywordsModeEnum.POPULARITY]: 'Popularity',
      },
      link: 'https://docs.empathy.co/explore-empathy-platform/analyze-search-and-discovery/use-terms.html?utm_source=carousel-3',
    },
    quickAccess: {
      title: 'My favorites',
      descriptions: {
        [FeatureIdEnum.EXPLAIN]:
          'Understand how product scoring affects product positioning on the SERP.',
        [FeatureIdEnum.PROMOTION]:
          'Create banners and promotions to highlight promotional search results on the SERP.',
        [FeatureIdEnum.CONFIGURATION]:
          'Boost or bury specific products or product attributes on the SERP to highlight or hide products.',
        [FeatureIdEnum.SYNONYM]:
          'Define alternative search terms for a query to bring relevant product results.',
        [FeatureIdEnum.EQUALIZE]:
          'Configure the product attribute weighting used in ranking the results to determine catalogue fields relevance.',
      },
      link: 'https://docs.empathy.co/explore-empathy-platform/overview/playboard-overview.html',
    },
    categories: {
      title: 'How are categories performing?',
      titleFiltered: 'How is the category performing?',
      date: 'Last week ({period})',
    },
    noData: {
      title: 'No data to display',
      titleQuickAccess: 'Display here your favorite Playboard tools’ shortcuts',
      titleWelcome: 'No activity for the last seven days to display',
      filters: 'Please select other filters',
      category: 'Please select another category',
    },
    documentationTitle: 'Want to know more?',
    tooltipButton: 'More options',
    documentationLink: eDocsPortal,
  },
}

const HIGHLIGHTS_MESSAGES_ES: HighlightsMessages = {
  headerTitle: 'You’re viewing:',
  filter: {
    default: 'All filters & all categories',
    title: {
      [HighlightsFilterEnum.GLOBAL]: 'Global',
      [HighlightsFilterEnum.FILTER]: 'Filters',
      [HighlightsFilterEnum.CATEGORY]: 'Categories',
    },
    maxFiltersAllowed: {
      [HighlightsFilterEnum.FILTER]: 'Add {maxFilters} filters max.',
      [HighlightsFilterEnum.CATEGORY]: 'Add {maxFilters} categories max.',
    },
    maxFiltersReached: {
      [HighlightsFilterEnum.FILTER]: '{maxFilters} filters already added.',
      [HighlightsFilterEnum.CATEGORY]: '{maxFilters}  categories already added.',
    },
    disabled: 'One or more elements of this filter no longer exist.',
  },
  aside: {
    filters: {
      title: 'Filters',
      accept: 'Save',
      duplicatedFilterError: 'This filter combination already exists.',
    },
    categories: {
      title: 'Categories',
      accept: 'Save',
      duplicatedCategoryError: 'The category selected is already a filter.',
      idLabel: '(ID: {idLabel} )',
    },
  },
  widget: {
    welcome: {
      titles: {
        morning: 'Buenos dias',
        afternoon: 'Buenas tardes',
        link: eDocsPortal,
      },
      description:
        'You can review activity data, identify opportunities for improvement, and take action to optimize the search experience!',
      link: 'https://docs.empathy.co/play-with-empathy-platform',
      today: 'Today',
      audit: {
        default: '{feature} {action}',
        simple: '{feature} {action} for {query}',
        multiple: '{feature} for {query} and {count} {action}',
        actions: {
          [AuditActionEnum.CREATE]: 'created',
          [AuditActionEnum.UPDATE]: 'updated',
          [AuditActionEnum.DELETE]: 'deleted',
          [AuditActionEnum.DOWNLOAD]: 'downloaded',
          [AuditActionEnum.SEARCH]: 'searched',
        },
      },
    },
    opportunities: {
      title: 'My opportunities',
      description:
        'Discover how to optimize your search performance' +
        ' and relevance leveraging the most appropriate Empathy ' +
        'Platform Playboard tool based on different search KPIs',
      actions: {
        close: 'Cerrar',
      },
      date: 'Hoy',
      tooltip: {
        tools: {
          title: 'Why don’t you use…?',
          [FeatureIdEnum.PROMOTION]: {
            clicks:
              'Products on the first results page aren’t relevant to your shoppers.<br>' +
              'TIP! Feature relevant products on top of the SERP.',
            findability:
              'Too many results for shoppers to find what they want?<br>TIP! Make relevant products stand out on top of the SERP.',
          },
          [FeatureIdEnum.CONFIGURATION]: {
            clicks:
              'No clicks for the products on the first result page?<br>' +
              'TIP! Boost relevant products to the top.',
            findability:
              'Shoppers can’t find easily what they’re looking for on the SERP.<br>' +
              'TIP! Boost relevant products to the first positions.',
          },
          [FeatureIdEnum.SYNONYM]: {
            noResults:
              'The query returns few results.<br>TIP! Try to associate alternative search terms with the query.',
            clicks:
              'The results for this query aren’t pretty relevant.<br>TIP! Define alternative search terms to the query.',
          },
          [FeatureIdEnum.RELATED_TAGS]:
            'Shoppers use broad queries that lead to loads of results.<br>' +
            'TIP! Set up related tags to help shoppers refine the query.',
          [FeatureIdEnum.REDIRECTION]:
            'Shoppers are looking for topics that aren’t related to your catalogue.<br>' +
            'TIP! Redirect shoppers to a relevant URL.',
        },
        evolution: {
          title: 'Query performance evolution',
        },
      },
    },
    kpis: {
      title: 'How is search performing?',
      date: 'Last week ({period})',
      link: 'https://docs.empathy.co/explore-empathy-platform/diagram/play/play-analytics.html',
    },
    now: {
      title: '¿Qué están buscando los clientes?',
      time: 'Últimos 15 mins',
      link: 'https://docs.empathy.co/explore-empathy-platform/analyze-search-and-discovery/use-now.html?utm_source=carousel-1',
    },
    keywords: {
      title: 'What are the latest trends?',
      subtitle: 'Top {count} terms by {mode}',
      modes: {
        [KeywordsModeEnum.GROWTH]: 'Growth',
        [KeywordsModeEnum.POPULARITY]: 'Popularity',
      },
      link: 'https://docs.empathy.co/explore-empathy-platform/analyze-search-and-discovery/use-terms.html?utm_source=carousel-3',
    },
    quickAccess: {
      title: 'My favorites',
      descriptions: {
        [FeatureIdEnum.EXPLAIN]:
          'Understand how product scoring affects product positioning on the SERP.',
        [FeatureIdEnum.PROMOTION]:
          'Create banners and promotions to highlight promotional search results on the SERP.',
        [FeatureIdEnum.CONFIGURATION]:
          'Boost or bury specific products or product attributes on the SERP to highlight or hide products.',
        [FeatureIdEnum.SYNONYM]:
          'Define alternative search terms for a query to bring relevant product results.',
        [FeatureIdEnum.EQUALIZE]:
          "Configure the product attribute weighting used in ranking the results to determine catalogue fields' relevance.",
      },
      link: 'https://docs.empathy.co/explore-empathy-platform/overview/playboard-overview.html',
    },
    categories: {
      title: 'How are categories performing?',
      titleFiltered: 'How is the category performing?',
      date: 'Last week ({period})',
    },
    noData: {
      title: 'No data to display',
      titleQuickAccess: 'Display here your favorite Playboard tools’ shortcuts',
      titleWelcome: 'No activity for the last seven days to display',
      filters: 'Please select other filters',
      category: 'Please select another category',
    },
    documentationTitle: '¿Quieres saber más?',
    tooltipButton: 'More options',
    documentationLink: eDocsPortal,
  },
}

export const HIGHLIGHTS_MESSAGES: Dictionary<{ highlights: HighlightsMessages }> = {
  [LocaleEnum.ENGLISH]: {
    highlights: HIGHLIGHTS_MESSAGES_EN,
  },
  [LocaleEnum.SPANISH]: {
    highlights: HIGHLIGHTS_MESSAGES_ES,
  },
}
